import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Worn-ink distress mask: fine fractal noise mapped to alpha so the red
// "ink" shows patchy/faded gaps like a real rubber stamp. Inlined as a data
// URI so the archived site still makes zero network requests.
const distress =
	"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -0.55 1.12'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

// Full-viewport "ARCHIVED" rubber stamp layered over the whole site.
// Visual-only: pointer-events are disabled so the page beneath stays readable.
export default function ArchivedOverlay() {
	return (
		<Box
			aria-hidden
			sx={{
				position: 'fixed',
				inset: 0,
				zIndex: 2000,
				// Click-through so the page behind still scrolls naturally.
				// Interaction with the page's own links/buttons is disabled in
				// Layout instead; the notice card below re-enables pointer events.
				pointerEvents: 'none',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: { xs: 4, md: 7 },
				px: 3,
				backgroundColor: 'rgba(255, 255, 255, 0.35)',
				backdropFilter: 'grayscale(45%)',
			}}
		>
			{/* Inline SVG filter that roughens the stamp's edges (ink bleed). */}
			<Box
				component='svg'
				width='0'
				height='0'
				sx={{ position: 'absolute' }}
				aria-hidden
			>
				<filter id='stampRough'>
					<feTurbulence
						type='fractalNoise'
						baseFrequency='0.02 0.04'
						numOctaves='3'
						seed='7'
						result='noise'
					/>
					<feDisplacementMap
						in='SourceGraphic'
						in2='noise'
						scale='5'
						xChannelSelector='R'
						yChannelSelector='G'
					/>
				</filter>
			</Box>

			{/* The stamp itself: double frame + distressed ink. */}
			<Box
				sx={{
					position: 'relative',
					transform: 'rotate(-13deg)',
					color: '#B5202A',
					border: '8px solid currentColor',
					borderRadius: '14px',
					px: { xs: 6, md: 12 },
					py: { xs: 3, md: 5 },
					opacity: 0.88,
					mixBlendMode: 'multiply',
					filter: 'url(#stampRough)',
					WebkitMaskImage: distress,
					maskImage: distress,
					WebkitMaskSize: '300px 300px',
					maskSize: '300px 300px',
					'&::before': {
						content: '""',
						position: 'absolute',
						inset: '5px',
						border: '2px solid currentColor',
						borderRadius: '7px',
					},
				}}
			>
				<Typography
					sx={{
						fontWeight: 800,
						letterSpacing: { xs: '0.12em', md: '0.22em' },
						lineHeight: 1,
						fontSize: { xs: '15vw', md: '8vw' },
						textTransform: 'uppercase',
						textShadow: '0 0 1px currentColor',
					}}
				>
					Archived
				</Typography>
			</Box>

			{/* Clean, readable human note — solid card so it stays legible
			    over whatever page content sits behind the overlay. */}
			<Box
				sx={{
					position: 'relative',
					zIndex: 1,
					pointerEvents: 'auto',
					textAlign: 'center',
					maxWidth: { xs: '90vw', md: '640px' },
					backgroundColor: '#ffffff',
					borderRadius: '14px',
					boxShadow: '0 10px 30px rgba(31, 31, 31, 0.18)',
					px: { xs: 3, md: 5 },
					py: { xs: 3, md: 4 },
				}}
			>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: { xs: '4.6vw', md: '1.6rem' },
						color: '#1F1F1F',
					}}
				>
					Thank you to everyone who used this service over the years.
				</Typography>
				<Typography
					sx={{
						mt: 1.5,
						fontWeight: 500,
						fontSize: { xs: '3.6vw', md: '1.05rem' },
						lineHeight: 1.5,
						color: 'rgba(31, 31, 31, 0.78)',
					}}
				>
					Sadly, I can no longer support it financially, so the snapshots are
					no longer being maintained or updated. It's been a pleasure serving
					the Cardano community. 💙
				</Typography>
				<Typography
					sx={{
						mt: 2,
						fontWeight: 500,
						fontSize: { xs: '3.6vw', md: '1.05rem' },
						lineHeight: 1.5,
						color: 'rgba(31, 31, 31, 0.78)',
					}}
				>
					Truly yours,
					<br />
					<Box
						component='a'
						href='https://x.com/stakepool247'
						target='_blank'
						rel='noopener noreferrer'
						sx={{
							color: 'primary.main',
							fontWeight: 700,
							textDecoration: 'underline',
							'&:hover': { color: 'primary.light' },
						}}
					>
						StakePool247
					</Box>
				</Typography>
				<Typography
					sx={{
						mt: 2.5,
						pt: 2.5,
						borderTop: '1px solid rgba(31, 31, 31, 0.12)',
						fontWeight: 500,
						fontSize: { xs: '3.6vw', md: '1.05rem' },
						lineHeight: 1.5,
						color: 'rgba(31, 31, 31, 0.78)',
					}}
				>
					To bootstrap a Cardano node quickly, we recommend{' '}
					<Box
						component='a'
						href='https://docs.cardano.org/developer-resources/scalability-solutions/mithril'
						target='_blank'
						rel='noopener noreferrer'
						sx={{
							color: 'primary.main',
							fontWeight: 700,
							textDecoration: 'underline',
							'&:hover': { color: 'primary.light' },
						}}
					>
						Mithril
					</Box>{' '}
					&mdash; the official fast-sync protocol with certified snapshots.
				</Typography>
			</Box>
		</Box>
	);
}
